import { useCallback } from "react";

import useSessionStore from "./useSessionStore";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { IS_MOCKED_SESSION } from "@features/session/constants";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import type { BackendSessionResponse } from "@memorang/types";
import { useToast } from "@memorang/ui/components/Toast";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import { fetchSessionItems } from "../api";
import type { ResumeSessionRequest } from "../api/types";
import { isIntroScreen } from "../helpers/intro-screens";
import { reconcileSummativeAssessments } from "../helpers/reconcile-summative-assessments";
import { findLastUpdatedItemIndex } from "../helpers/session";
import { flattenSessionItems } from "../helpers/session";

export const useResumeSession = () => {
	const router = useRouter();
	const { showToast } = useToast();
	const { userId } = useAuthContext();
	const {
		currentExamContext: { organizationId, projectId },
	} = useExamContext();

	const resumeSessionMutation = useMutation({
		mutationFn: fetchSessionItems,
	});

	const resumeSession = useCallback(
		(request: ResumeSessionRequest) => {
			return resumeSessionMutation.mutateAsync(request);
		},
		[resumeSessionMutation],
	);
	const updateCurrentBlockIndex = useSessionStore(
		(state) => state.updateCurrentBlockIndex,
	);

	const updateProgress = useSessionStore((state) => state.updateProgress);
	const updateProgressForEachBlock = useSessionStore(
		(state) => state.updateProgressForEachBlock,
	);
	const updateSession = useSessionStore((state) => state.updateSession);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const handleResumeSession = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: resumes mocked and live sessions, including reconciliation and progress bookkeeping
		async (numericId: number, options?: { replaceRoute?: boolean }) => {
			// Reset first: warm resume reuses the JS context, so prior-session state can leak.
			resetSessionStore();
			try {
				let result: BackendSessionResponse;
				if (IS_MOCKED_SESSION) {
					const { mockedDiagnosticSession } = await import(
						"@features/session/constants/mocked-diagnostic-session"
					);
					const { data } = mockedDiagnosticSession;
					result = data as unknown as BackendSessionResponse;
				} else {
					result = await resumeSession({
						sessionId: numericId,
					});
				}
				const flattenedSessionItems = flattenSessionItems(
					result.sessionItems,
					result.sessionType !== "FORMATIVE",
				);

				if (!flattenedSessionItems.length) {
					const error = new Error(
						"Error resuming session: session list is empty",
					);
					showToast({
						message: "Failed to resume session, try again",
					});
					captureErrorToSentry(error, {
						sessionId: numericId,
						error: serializeErrorForSentry(result),
					});
					router.replace("/home");
					return true;
				}

				const { lastUpdatedBlockIndex, lastUpdatedItemIndex } =
					findLastUpdatedItemIndex(flattenedSessionItems);

				// A fresh resume lands on the pre-test intro block, which has no
				// question items — fall back to the screen count so it stays valid.
				const resumeBlock = flattenedSessionItems[lastUpdatedBlockIndex] ?? [];
				const numItems =
					resumeBlock.filter((item) => !isIntroScreen(item)).length ||
					resumeBlock.length;
				if (!numItems) {
					const error = new Error(
						"Error resuming session: flattened session list is empty",
					);
					showToast({
						message: "Failed to resume session, try again",
					});
					captureErrorToSentry(error, {
						sessionId: numericId,
						error: serializeErrorForSentry(result),
					});
					router.replace("/home");
					return true;
				}

				const { status } = result;

				const sessionResponse = {
					...result,
					sessionItems: flattenedSessionItems,
				};

				if (status === "COMPLETE") {
					router.replace(`/session/${numericId}/report/`);
				} else {
					updateCurrentBlockIndex(lastUpdatedBlockIndex);

					updateSession({
						sessionResponse,
						numItemsRequested: numItems,
						isSummativeTest: result.sessionType !== "FORMATIVE",
						isDiagnostic: result.sessionSubType === "DIAGNOSTIC",
						isResumedSession: true,
					});
					if (userId) {
						reconcileSummativeAssessments({
							userId,
							projectId,
							organizationId,
						}).catch(() => undefined);
					}
					if (lastUpdatedBlockIndex > 0) {
						for (let i = 0; i < lastUpdatedBlockIndex; i++) {
							updateProgressForEachBlock(1, i);
						}
					}

					const progress = lastUpdatedItemIndex / numItems;
					updateProgress(progress);

					// Replace when the session screen is already mounted (refresh/deep-link):
					// pushing stacks a duplicate screen whose frozen index param keeps
					// rendering a stale Portal FAB above the live screen.
					const navigate = options?.replaceRoute ? router.replace : router.push;
					navigate({
						pathname: "/(protected)/session/[id]",
						params: {
							id: sessionResponse.numericId,
							index: String(lastUpdatedItemIndex) || "1",
						},
					});
				}
				return true;
			} catch (_error) {
				showToast({
					message: "Failed to resume session, try again",
				});
				const error = new Error("Error resuming session");
				captureErrorToSentry(error, {
					sessionId: numericId,
					error: serializeErrorForSentry(_error),
				});
				return false;
			}
		},
		[
			router,
			showToast,
			updateCurrentBlockIndex,
			updateProgress,
			updateSession,
			resumeSession,
			updateProgressForEachBlock,
			resetSessionStore,
			userId,
			projectId,
			organizationId,
		],
	);

	return {
		handleResumeSession,
	};
};
